import { useWindowSize } from "hooks/useWindowSize";
import DashboardContainer from "components/dashboard/DashboardContainer";
import { Divider } from "components/design/Divider";
import Async from "components/DesignSystem/AsyncComponents/Async";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { PaginationHandler } from "components/DesignSystem/Pagination/Pagination";
import { RefreshButton } from "components/DesignSystem/RefreshButton/RefreshButton";
import { REFRESH_PAGE } from "constants/refreshPage";
import { Search } from "components/DesignSystem/Search/Search";
import { ExportOptions } from "components/ExportOptions/ExportOptions";
import { EmptyInvoiceList } from "components/Illustrations/EmptyInvoiceList";
import { ArrowDown } from "components/icons/ArrowDown";
import { CopyIcon } from "components/icons/CopyIcon";
import { InvoiceConsole } from "components/InvoiceList/InvoiceList";
import { SetupInoviceSetting } from "components/SetupInoviceSetting/SetupInoviceSetting";
import { BillList } from "components/VendorBills/BillList";
import { WithholdingSummaryCard } from "components/VendorBills/WithholdingSummaryCard";
import {
  BILL_STATUS,
  BILL_STATUS_GROUP_TYPES_TYPE,
  FINANCIAL_RECORD_TYPE,
} from "constants/vendorBills";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useFilters } from "hooks/useFilter";
import { useQuery, useUpdateQuery } from "hooks/useQuery";
import { EmptyScreen } from "pages/Books/EmptyScreen";
import { RowSelectionState, SortingState } from "react-table-8.10.7";
import { useState } from "react";
import { useGetInvoiceSettingsQuery } from "store/apis/invoices";
import {
  useGetAllBillsQuery,
  useLazyGetAllBillsQuery,
} from "store/apis/vendorBills";
import { useDebounce } from "utils/debounce";
import { openLink } from "utils/openLink";
import { VendorBillsFilter } from "./VendorBillsFilter";
import { useLazyGetPreviewUrlQuery } from "store/apis/previewUrl";
import { useToast } from "hooks/useToast";
import { Button } from "components/DesignSystem/Button/Button";

const BILL_REFRESH_ENDPOINTS = ["getAllBills"];

export type GetBillsOptions = {
  groupId: string;
  entityId: string;
  bill_type: keyof typeof FINANCIAL_RECORD_TYPE;
  page_num?: number;
};

type VendorBillsProps = {
  entityId: string;
  canRequestAndAddBill?: boolean;
  isSearchable?: boolean;
  merchantId?: string;
  rowSelection: RowSelectionState;
  setRowSelection: (
    updaterOrValue:
      | RowSelectionState
      | ((prev: RowSelectionState) => RowSelectionState)
  ) => void;
};

export const VendorBills = ({
  entityId,
  canRequestAndAddBill = true,
  isSearchable = true,
  merchantId,
  rowSelection,
  setRowSelection,
}: VendorBillsProps) => {
  const { uuid: groupId } = useCurrentGroupContext();
  const { successToast, alertToast } = useToast();
  const [exportBills, { isLoading: isExporting }] = useLazyGetAllBillsQuery();
  const { isMobile } = useWindowSize();
  const [getPreviewUrl] = useLazyGetPreviewUrlQuery();
  const { values, updateFilter } = useFilters<{
    BILL_START_DATE: string;
    BILL_END_DATE: string;
    DUEDATE_START_DATE: string;
    DUEDATE_END_DATE: string;
    BILL_DATE: string;
    DUE_DATE: string;
    SOURCE: string[];
    STATUS: (keyof typeof BILL_STATUS)[];
    STATUS_GROUP: BILL_STATUS_GROUP_TYPES_TYPE | null;
    VENDOR: string;
    VENDOR_STATE: string[];
    MIN_AMOUNT: string | "";
    MAX_AMOUNT: string | "";
    SEARCH: string | null;
    BILL_TYPE: keyof typeof FINANCIAL_RECORD_TYPE;
    PAGE_NUM: number;
  }>({
    initialValue: {
      BILL_START_DATE: "",
      BILL_END_DATE: "",
      DUEDATE_START_DATE: "",
      DUEDATE_END_DATE: "",
      BILL_DATE: "",
      DUE_DATE: "",
      SOURCE: [],
      STATUS: [] as (keyof typeof BILL_STATUS)[],
      STATUS_GROUP: null,
      VENDOR: "",
      VENDOR_STATE: [],
      MIN_AMOUNT: "",
      MAX_AMOUNT: "",
      SEARCH: null,
      BILL_TYPE: FINANCIAL_RECORD_TYPE.BILL,
      PAGE_NUM: 1,
    },
    useQueryParams: true,
  });

  const {
    data: invoiceSetting,
    isSuccess: settingsLoaded,
    isLoading: settingsLoading,
  } = useGetInvoiceSettingsQuery(
    {
      groupId: groupId,
      entityId,
    },
    { skip: !groupId || !entityId }
  );

  const pageNum = useDebounce(values.PAGE_NUM);
  const search = useDebounce(values.SEARCH);

  const query = useQuery();
  const pageSize = query.get("page_size");

  const handlePageNumChange = (page: number) => {
    updateFilter("PAGE_NUM", page);
  };

  const [sorting, setSorting] = useState<SortingState>([
    {
      id: "INVOICE_DATE",
      desc: true,
    },
  ]);

  const sortCol = sorting[0]?.id as "INVOICE_DATE" | undefined;
  const sortOrder = sorting[0]?.desc;

  const { data, isSuccess, isLoading, isFetching } = useGetAllBillsQuery(
    {
      bill_type: values.BILL_TYPE,
      entityId,
      groupId,
      page_num: pageNum,
      search_term: search,
      merchant_id: merchantId,
      minAmount: values.MIN_AMOUNT,
      maxAmount: values.MAX_AMOUNT,
      startDate: values.BILL_START_DATE,
      endDate: values.BILL_END_DATE,
      dueDateStartDate: values.DUEDATE_START_DATE,
      dueDateEndDate: values.DUEDATE_END_DATE,
      statuses: values.STATUS,
      statusGroup: values.STATUS_GROUP,
      customer_uuids: values.VENDOR,
      customerStates: values.VENDOR_STATE,
      sources: values.SOURCE,
      sortCol,
      sortOrder:
        sortOrder !== undefined
          ? sortOrder
            ? ("DSC" as const)
            : ("ASC" as const)
          : undefined,
      limit: pageSize ? Number(pageSize) : undefined,
    },
    {
      skip: !groupId || !entityId,
    }
  );

  const {
    bills = [],
    due_amount,
    overdue_amount,
    total_amount,
    paid_amount,
    per_page = 1,
    total_count = 1,
    total_pages = 1,
    withholding_summary,
  } = data || {};

  const handlesearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateFilter("SEARCH", e.target.value || null);
  };

  const handleExport = async ({
    exportToEmail,
  }: {
    exportToEmail: boolean;
  }) => {
    try {
      const result = await exportBills({
        groupId: groupId!,
        entityId,
        bill_type: values.BILL_TYPE,
        page_num: 1,
        search_term: search,
        merchant_id: merchantId,
        minAmount: values.MIN_AMOUNT,
        maxAmount: values.MAX_AMOUNT,
        startDate: values.BILL_START_DATE,
        endDate: values.BILL_END_DATE,
        dueDateStartDate: values.DUEDATE_START_DATE,
        dueDateEndDate: values.DUEDATE_END_DATE,
        statuses: values.STATUS,
        statusGroup: values.STATUS_GROUP,
        sources: values.SOURCE,
        customerStates: values.VENDOR_STATE,
        generate_report: true,
        export_to_email: exportToEmail,
      }).unwrap();

      if (exportToEmail) {
        successToast({
          message: "Bills export initiated. You will receive an email shortly",
        });
        return;
      }

      if (result.excel_document_id) {
        const { download_url } = await getPreviewUrl({
          groupId: groupId!,
          fileId: result.excel_document_id,
        }).unwrap();
        openLink(download_url);
      }

      successToast({ message: "Bills exported successfully" });
    } catch {
      alertToast({ message: "Error exporting bills" });
    }
  };

  const { update } = useUpdateQuery();

  const refreshButton = (
    <RefreshButton
      endpoints={BILL_REFRESH_ENDPOINTS}
      page={REFRESH_PAGE.BILLS}
    />
  );

  return (
    <Async.Root
      isLoading={isLoading || settingsLoading || !settingsLoaded}
      isSuccess={isSuccess && settingsLoaded}
      isEmpty={settingsLoaded && !invoiceSetting}
    >
      <Async.Empty>
        <SetupInoviceSetting context="bills" />
      </Async.Empty>
      <Async.Success>
        <DashboardContainer className="t-relative t-h-full sm:t-h-auto">
          <DashboardContainer.Content className="t-flex t-flex-col t-gap-5 t-h-full t-min-h-0 t-overflow-hidden sm:t-h-auto sm:t-overflow-visible">
            <div className="t-bg-surface t-flex t-flex-col t-gap-4">
              <div className="t-flex t-items-stretch t-rounded t-border-solid t-border-neutral-0 t-border sm:t-flex-col">
                <InvoiceConsole
                  bordered={false}
                  total={{
                    label: "Total billed amount",
                    amount: total_amount || 0,
                  }}
                  paid={{
                    label: "Paid",
                    amount: paid_amount || 0,
                  }}
                  due={{
                    label: "Outstanding",
                    amount: due_amount || 0,
                    overdueAmount: overdue_amount || 0,
                  }}
                />
                {withholding_summary && (
                  <div className="t-flex t-flex-1 t-items-stretch t-justify-center">
                    <Divider
                      orientation="vertical"
                      fullHeight
                      classNames="sm:t-hidden"
                    />
                    <div className="t-p-5 sm:t-p-3">
                      <WithholdingSummaryCard summary={withholding_summary} />
                    </div>
                  </div>
                )}
              </div>
              <div className="t-flex t-justify-between t-items-center sm:t-flex-col sm:t-items-stretch sm:t-gap-2">
                <div className="t-flex t-gap-2 t-w-full t-items-center t-flex-wrap sm:t-flex-nowrap">
                  {isSearchable && (
                    <Search
                      placeholder="Search bills"
                      className="t-w-[18%] focus:t-w-3/6 t-transition-all sm:t-w-auto sm:t-min-w-0 sm:t-flex-1 sm:focus:t-w-auto"
                      onChange={handlesearch}
                      defaultValue={search || ""}
                      isLoading={isFetching}
                    />
                  )}
                  <VendorBillsFilter
                    filterValues={values}
                    updateFilter={updateFilter}
                  />
                  {isMobile && refreshButton}
                </div>
                <div className="t-flex t-flex-col t-items-end t-gap-2 t-shrink-0 sm:t-items-stretch">
                  <div className="t-flex t-gap-2 t-items-center sm:t-flex-wrap">
                    {invoiceSetting?.bill_forwarding_email && (
                      <Button
                        customType="secondary"
                        size="xsmall"
                        leftIcon={<CopyIcon className="t-text-text-30" />}
                        onClick={() => {
                          navigator.clipboard.writeText(
                            invoiceSetting.bill_forwarding_email!
                          );
                          successToast({
                            title: "Email copied",
                            message:
                              "Bill forwarding email copied to clipboard",
                          });
                        }}
                      >
                        Bill forwarding email
                      </Button>
                    )}
                    {merchantId && (
                      <Dropdown.Root>
                        <Dropdown.Trigger asChild>
                          <div>
                            <Button
                              customType="secondary"
                              size="xsmall"
                              isLoading={isExporting}
                              disabled={isExporting}
                            >
                              <div className="t-flex t-items-center t-gap-1.5">
                                Export outstanding bills
                                <div className="group-data-state-open:-t-rotate-180 t-transform t-transition t-duration-300 t-ease-in-out">
                                  <ArrowDown color="currentColor" />
                                </div>
                              </div>
                            </Button>
                          </div>
                        </Dropdown.Trigger>
                        <Dropdown.Portal>
                          <Dropdown.Content sideOffset={4} align="end">
                            <ExportOptions
                              onLocalDownload={() =>
                                handleExport({ exportToEmail: false })
                              }
                              onMailExport={() =>
                                handleExport({ exportToEmail: true })
                              }
                            />
                          </Dropdown.Content>
                        </Dropdown.Portal>
                      </Dropdown.Root>
                    )}
                  </div>
                  <div className="t-flex t-items-center t-gap-2 sm:t-justify-end">
                    <PaginationHandler
                      page_num={Number(values.PAGE_NUM) || 1}
                      per_page={per_page}
                      total_count={total_count}
                      total_pages={total_pages}
                      onPageNumChange={handlePageNumChange}
                      isLoading={isFetching}
                      showOptions
                      pageQueryKey="PAGE_NUM"
                    />
                    {!isMobile && refreshButton}
                  </div>
                </div>
              </div>
            </div>

            <div className="t-flex-1 t-min-h-0 t-overflow-hidden sm:t-flex-none sm:t-overflow-visible sm:t-pt-2">
              <Async.Root
                isLoading={isLoading}
                isSuccess={isSuccess}
                isEmpty={bills.length === 0}
              >
                <Async.Empty>
                  <EmptyScreen text="No bills found">
                    <EmptyInvoiceList />
                  </EmptyScreen>
                </Async.Empty>
                <Async.Success>
                  <BillList
                    bills={bills}
                    rowSelection={rowSelection}
                    setRowSelection={setRowSelection}
                    sorting={sorting}
                    setSorting={setSorting}
                    updateFilter={updateFilter}
                    showWithholdingColumn={bills.some(
                      (bill) => bill.withholding_status
                    )}
                  />
                </Async.Success>
              </Async.Root>
            </div>
          </DashboardContainer.Content>
        </DashboardContainer>
      </Async.Success>
    </Async.Root>
  );
};
